import { useAuth } from '../context/AuthContext';
import { Button, EmptyState, FormField, Icon } from '../components/ui';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiError } from '../api/client';
import DirPicker from '../components/DirPicker';
import { useCapabilities } from '../hooks/useCapabilities';
import { statusBadge } from '../lib/format';

export default function ProjectsPage() {
  const { user } = useAuth();
  const capabilities = useCapabilities();
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', workspace_path: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showDirPicker, setShowDirPicker] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setError(null);
    api.listProjects().then(setProjects).catch(err => setError(apiError(err))).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const project = await api.createProject({
        title: form.title,
        description: form.description,
        ...(capabilities.workspace_configuration ? { workspace_path: form.workspace_path || undefined } : {}),
      });
      setShowForm(false);
      setForm({ title: '', description: '', workspace_path: '' });
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="eyebrow">От идеи к результату</div><h1 className="page-title">Проекты</h1>
          <p className="page-subtitle">Исследуйте идеи, работайте с агентами и собирайте результаты в одном месте.</p></div>
        <Button className="btn btn-primary" disabled={user?.moderation?.restricted} onClick={() => setShowForm(!showForm)}>
          <Icon name={showForm ? 'close' : 'plus'} size={18} />{showForm ? 'Отмена' : 'Новый проект'}
        </Button>
      </div>

      {showForm && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: 16 }}>Создать проект</h2>
          <form onSubmit={handleCreate}><fieldset disabled={user?.moderation?.restricted} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <FormField label={<>Название</>}>
            <input className="form-control" value={form.title} onChange={e => setForm({
                ...form,
                title: e.target.value
              })} placeholder="Название проекта" required />
            </FormField>
            <FormField label={<>Идея / описание</>}>
            <textarea className="form-control" value={form.description} onChange={e => setForm({
                ...form,
                description: e.target.value
              })} placeholder="Опишите вашу идею..." />
            </FormField>
            {capabilities.workspace_configuration && <div className="form-group">
              <label className="form-label" htmlFor="new-project-workspace">Рабочая директория (для агента-разработчика)</label>
              <div className="flex gap-2">
                <input
                  id="new-project-workspace"
                  className="form-control"
                  value={form.workspace_path}
                  readOnly
                  placeholder="Папка не выбрана"
                  style={{ flex: 1, cursor: 'default', background: 'var(--bg-secondary)' }}
                />
                <Button type="button" className="btn" onClick={() => setShowDirPicker(true)} style={{ whiteSpace: 'nowrap' }}>
                  Выбрать…
                </Button>
                {form.workspace_path && (
                  <Button type="button" className="btn" aria-label="Очистить рабочую папку" onClick={() => setForm({ ...form, workspace_path: '' })} style={{ padding: '0 10px', color: 'var(--text-muted)' }}>×</Button>
                )}
              </div>
            </div>
            }
            {capabilities.workspace_configuration && showDirPicker && (
              <DirPicker
                value={form.workspace_path}
                onSelect={p => setForm({ ...form, workspace_path: p })}
                onClose={() => setShowDirPicker(false)}
              />
            )}
            <Button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Создание...' : 'Создать'}
            </Button>
          </fieldset></form>
        </div>
      )}

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : error ? (
        <div role="alert" className="alert alert-error">{error} <Button className="btn" onClick={load}>Повторить</Button></div>
      ) : projects.length === 0 ? (
        <EmptyState title="Начните с вашей идеи" icon="projects"
          action={!showForm && <Button className="btn btn-primary" disabled={user?.moderation?.restricted} onClick={() => setShowForm(true)}><Icon name="plus" size={18} />Создать первый проект</Button>}>
          Создайте проект, опишите задачу и поручите исследование команде агентов.
        </EmptyState>
      ) : (
        <div className="grid-2">
          {projects.map(p => (
            <Link to={`/projects/${p.id}`} key={p.id} className="card project-card">
              <span className="project-card-icon"><Icon name="projects" /></span>
              <div className="card-header">
                <span className="card-title">{p.title}</span>
                {statusBadge(p.status)}
              </div>
              {p.description && <p className="project-description">{p.description}</p>}
              <div className="project-card-footer"><span>{new Date(p.created_at).toLocaleDateString('ru')}</span><Icon name="arrow" size={18} /></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
